import React, { useState } from 'react';
import { ArrowRight, Shield, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { projectStatusOptions } from '../../data/mock';

const LeadFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    projectStatus: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, projectStatus: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/mdaldqao', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          email: formData.email,
          projectStatus: formData.projectStatus,
          description: formData.description
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          company: '',
          email: '',
          projectStatus: '',
          description: ''
        });
      } else {
        alert('Es gab ein Problem beim Senden. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Es gab ein Problem beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="lead-form" className="py-12 sm:py-16 lg:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 text-xs sm:text-sm font-medium mb-3 sm:mb-4 border border-cyan-200">
            Kontakt aufnehmen
          </span>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-800 mb-3 sm:mb-4 px-4">
            Ihr Quartier energie- und renditestark machen
          </h2>
          <p className="text-sm sm:text-base text-slate-600 px-4">
            Fordern Sie jetzt Ihre kostenlose Erstbewertung an.
          </p>
        </div>

        {/* Form Container */}
        <div className="relative p-5 sm:p-8 md:p-10 rounded-xl sm:rounded-2xl bg-slate-50 border border-slate-200">
          {isSubmitted ? (
            <div className="text-center py-8 sm:py-12">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2">Vielen Dank!</h3>
              <p className="text-sm sm:text-base text-slate-600 px-4">
                Wir haben Ihre Anfrage erhalten und melden uns in Kürze.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Name & Company Row */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Ihr Name"
                    className="bg-white border-slate-300 rounded-lg h-11 sm:h-12 text-slate-800 placeholder:text-slate-400 focus:border-cyan-500 focus:ring-cyan-500 text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Unternehmen *
                  </label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder="Ihr Unternehmen"
                    className="bg-white border-slate-300 rounded-lg h-11 sm:h-12 text-slate-800 placeholder:text-slate-400 focus:border-cyan-500 focus:ring-cyan-500 text-sm sm:text-base"
                  />
                </div>
              </div>

              {/* Email & Project Status Row */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    E-Mail *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="ihre@email.de"
                    className="bg-white border-slate-300 rounded-lg h-11 sm:h-12 text-slate-800 placeholder:text-slate-400 focus:border-cyan-500 focus:ring-cyan-500 text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Projektstatus
                  </label>
                  <Select onValueChange={handleSelectChange} value={formData.projectStatus}>
                    <SelectTrigger className="bg-white border-slate-300 rounded-lg h-11 sm:h-12 text-slate-800 focus:ring-cyan-500 text-sm sm:text-base">
                      <SelectValue placeholder="Auswählen..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg">
                      {projectStatusOptions.map((option) => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value}
                          className="text-slate-800 hover:bg-slate-100 focus:bg-slate-100 cursor-pointer text-sm sm:text-base"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                  Projektbeschreibung
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Erzählen Sie uns von Ihrem Projekt..."
                  className="bg-white border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:border-cyan-500 focus:ring-cyan-500 resize-none text-sm sm:text-base"
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-cyan-700 text-white hover:bg-cyan-800 font-medium text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-5 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed group"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span className="ml-2">Wird gesendet...</span>
                  </>
                ) : (
                  <>
                    <span>Projekt prüfen lassen</span>
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>

              {/* Trust Line */}
              <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-slate-500">
                <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                <span>Kostenlos & vertraulich</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadFormSection;
